/**
 * BankBalancesPanel — bank balances and treasury, on the Bank Feed page
 * =====================================================================
 * Usability review of 23 October 2026, item 14: *"Move the bank and treasury
 * sections onto the Bank Feed page. They make the Dashboard very long, and the
 * Fund / Mgmt Co. toggle there only applies to part of the page, which is
 * confusing."* Moved whole from the Dashboard: the same figures, the same
 * toggle, and here the toggle governs everything in the panel it sits in.
 */
import React, { useState } from "react";
import useCachedGet from "./useCachedGet";
import BankingOverview from "./BankingOverview";
import { bankingSummary, bankingAtLevel } from "./bankingSummary";
import { FreshnessBadge, InfoTip } from "./PageTools";
import { money, day } from "./format";
import t from "./theme";
import "../pages/Dashboard.css";

function TreasuryMetric({ label, value, tone, tip }) {
  return (
    <div className="treasury-metric">
      <div className="treasury-metric-label">{label}{tip && <InfoTip>{tip}</InfoTip>}</div>
      <div className={`treasury-metric-value${tone ? ` treasury-${tone}` : ""}`}>{value}</div>
    </div>
  );
}

const STORE = "pms.bankFeed.balancesOpen";
const recallOpen = () => {
  try { return window.localStorage.getItem(STORE) !== "false"; } catch { return true; }
};

export default function BankBalancesPanel() {
  const [level, setLevel] = useState("fund");
  const [open, setOpen] = useState(recallOpen);
  const toggle = () => {
    setOpen((v) => {
      try { window.localStorage.setItem(STORE, String(!v)); } catch { /* convenience */ }
      return !v;
    });
  };
  const treasuryQ = useCachedGet("/reports/treasury/");
  const mercuryQ = useCachedGet("/integrations/mercury/accounts/");
  const treasury = treasuryQ.data;
  const connections = Array.isArray(mercuryQ.data?.connections) ? mercuryQ.data.connections : [];
  const banking = bankingSummary(connections, treasury);
  const selected = bankingAtLevel(banking, level);
  const tres = treasury || {};
  const bankBal = banking.total;
  const staleBanks = tres.bank_balances_stale || [];
  const glCash = tres.gl_cash ?? tres.unencumbered_cash ?? null;
  const gap = bankBal != null && glCash != null ? bankBal - Number(glCash) : null;

  return (
    <section className="bank-balances-panel" aria-label="Bank balances and treasury">
      <div className="bank-balances-head">
        <button type="button" className="bank-balances-toggle" aria-expanded={open} onClick={toggle}>
          {open ? "▾" : "▸"} Bank balances &amp; treasury
        </button>
        <FreshnessBadge
          savedAt={Math.min(treasuryQ.savedAt || Infinity, mercuryQ.savedAt || Infinity) === Infinity ? null : Math.min(treasuryQ.savedAt || Infinity, mercuryQ.savedAt || Infinity)}
          refreshing={treasuryQ.refreshing || mercuryQ.refreshing}
          error={treasuryQ.error || mercuryQ.error}
          onRefresh={() => { treasuryQ.reload(); mercuryQ.reload(); }}
        />
      </div>
      {open && (
        <div className="bank-balances-body">
          <div className="dashboard-cash-selector" style={{ fontFamily: t.fontBody, color: t.text }}>
            <div className="dashboard-cash-switch" role="group" aria-label="Bank cash level" style={{ background: t.bgSubtle, borderColor: t.border }}>
              <span className="dashboard-cash-slider" aria-hidden="true" style={{ transform: `translateX(${level === "fund" ? 0 : 100}%)`, background: t.accent, boxShadow: t.shadowSm }} />
              {[["fund", "Fund"], ["management", "Mgmt. Co."]].map(([value, label]) =>
                <button key={value} type="button" aria-pressed={level === value} onClick={() => setLevel(value)}
                  style={{ color: level === value ? t.textOnAccent : t.textSecondary }}>{label}</button>)}
            </div>
            <div style={{ color: t.textMuted, fontSize: 12 }}>
              <strong style={{ color: t.text }}>{level === "fund" ? "Fund bank cash" : "Management company bank cash"}</strong>
              <div style={{ marginTop: 4 }}>{level === "fund" ? "Helena Global Investment Opportunities 1 Ltd · Citi HGIO1 · Citi HSS1" : "Helena Partners Inc · Mercury ending 6816 · Citi HPI"}</div>
            </div>
          </div>
          <BankingOverview summary={selected} />
          {selected.unassigned.length > 0 && <p style={{ color: t.warning, fontSize: 12 }}>Bank accounts awaiting a level assignment: {selected.unassigned.join(", ")}</p>}

          <h3 className="treasury-heading">Treasury <span>· all entities</span></h3>
          <div className="treasury-grid">
            <TreasuryMetric label="Cash at bank" value={money(bankBal, { whole: true })} />
            <TreasuryMetric label="Cash on the ledger" value={money(glCash, { whole: true })} tone={Number(glCash) === 0 ? "neg" : undefined}
              tip="Cash as the general ledger has it — what has been posted." />
            <TreasuryMetric label="Not yet posted" value={money(gap, { whole: true })} tone={Number(gap) > 0 ? "neg" : undefined}
              tip="Cash at the bank less cash on the ledger: bank transactions still waiting to be booked." />
            <TreasuryMetric label="ELOC receivables" value={money(tres.eloc_receivables ?? 0, { whole: true })} />
            <TreasuryMetric label="Opportunity cost" value={money(tres.opportunity_cost ?? 0, { whole: true })} tone="warn"
              tip="What the cash at bank would earn in 30 days at 5.2%." />
            <TreasuryMetric label="Unposted transactions" value={tres.unposted_count ?? "—"} />
          </div>
          {/* Where the bank figure comes from. Mercury answers over its API;
              Citi has none, so its balance is only as current as the last time
              somebody recorded one — which is worth stating rather than hiding
              inside a single total. */}
          <div className="treasury-sources">
            <span>Mercury <strong>{money(banking.mercuryTotal, { whole: true })}</strong></span>
            {(tres.bank_accounts || []).map((b) => (
              <span key={`${b.institution}-${b.account}`}>
                {b.institution} {b.account} <strong>{money(b.balance, { whole: true })}</strong>
                <span style={{ opacity: 0.7 }}> as of {day(b.as_of)}</span>
              </span>
            ))}
            {staleBanks.length > 0 && (
              <span style={{ color: t.warning }}>
                No balance on file for {staleBanks.map((b) => b.account).join(", ")} — not counted
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
